import { withMissingColumnConfigs } from '../../order-matching/print/print-columns'
import type { ColumnConfig } from '../../order-matching/print/svg/main-row'
import { columnConfigSchema, columnTypeLabel } from '../../order-matching/print/svg/main-row'

//
// ホーム（ミルシート一覧）「印刷・データ出力」専用の列構成。
// v1 apps/frontend/client/src/components/MillsheetPrintForm/MillsheetListSvgV2/MainRow.tsx の
// REORDERABLE_COLUMN_TYPES / FIXED_ORDER_COLUMN_TYPES / isOtherColumnType / fixColumnOrder の移植。
// 「集計表用（summaryColumnConfigs）」「使用鋼材一覧表 Excel 用（excelColumnConfigs）」を
// v1 と同じ localStorage キー・同じ既定値で別々に持つ。
// 一度 client v2 で1つの設定に統合したが、集計表（A4に横並びで収める帳票なので少数精鋭）と
// Excel（列が多くても困らない）では適切な項目数がまったく違い、片方を直すともう片方が崩れるため
// 2026/08/30 に v1 と同じ2本立てへ戻した。列の型・並び替えグループ自体は両者で共通。
//
// 加工証明（shearing）も同じ列定義・同じ既定値を使うため、この2つの初期値をそのまま共有する。
//

export type { ColumnConfig }

export const REORDERABLE_COLUMN_TYPES: Array<ColumnConfig['type']> = [
  'manufacturerName',
  'steelType',
  'sectionType',
  'no',
  'constructionArea',
  'member',
  'buildingPart',
  'orderNumber',
]

// 組織設定（テナント設定 millsheetSortConfig）の登録順で並ぶ項目
export const ORG_ORDERED_COLUMN_TYPES: Array<ColumnConfig['type']> = ['manufacturerName', 'steelType', 'sectionType']

export type PrintSortDirection = NonNullable<ColumnConfig['direction']>

// 並び替えできる項目の既定の並び方向（2026-10-19 オーナー決定）。
// 組織設定の登録順で並ぶ 3 項目は「登録順 = 昇順」、それ以外（証明書番号・工区・節・部位・発注番号）は
// 顧客要望「数値系の値は降順（大きいものが上）」に合わせて降順。比較は自然順なので数値の証明書番号・発注番号は大きい順になる。
// 既定を初期設定の direction に書き込まず、ここで決めて読む側で補うのは、localStorage に保存済みの
// direction 無しの列設定にも既定の変更が効くようにするため（明示的に保存された direction は尊重する）
export function defaultPrintSortDirection(type: ColumnConfig['type']): PrintSortDirection {
  return ORG_ORDERED_COLUMN_TYPES.includes(type) ? 'asc' : 'desc'
}

export function resolvePrintSortDirection(config: Pick<ColumnConfig, 'type' | 'direction'>): PrintSortDirection {
  return config.direction ?? defaultPrintSortDirection(config.type)
}

export const FIXED_ORDER_COLUMN_TYPES: Array<ColumnConfig['type']> = [
  'size',
  'thicknessDiameter',
  'length',
  'quantity',
  'weight',
  'heatNumber',
  'individualNumber',
  'memo',
]

// 数値として読む列。Excel で右寄せにして桁を縦に揃える（v1 NUMERIC_COLUMN_TYPES と同じ）
export const NUMERIC_COLUMN_TYPES: Array<ColumnConfig['type']> = ['length', 'quantity', 'weight']

// ページ番号・入れ替え可能・順番固定のどのグループにも属さない列（商社・内部番号）
export function isOtherColumnType(type: ColumnConfig['type']): boolean {
  return type !== 'pageNumber' && !REORDERABLE_COLUMN_TYPES.includes(type) && !FIXED_ORDER_COLUMN_TYPES.includes(type)
}

// 選べる列（並び替え可能・順番固定・その他の3グループの合計）。発注照合専用の列
// （endorsementCompany/customer/referenceNumber）はホームでは選択肢に出さない
export const SELECTABLE_COLUMN_TYPES: Array<ColumnConfig['type']> = [
  ...REORDERABLE_COLUMN_TYPES,
  ...FIXED_ORDER_COLUMN_TYPES,
  'tradingCompany',
  'innerNo',
]

// ホームの Excel（使用鋼材一覧表）だけで選べる列。裏書は集計表（A4に収める帳票）には出さない方針で、
// 加工証明も同じ SELECTABLE_COLUMN_TYPES を共有するため、共有側には足さずここで足す
export const HOME_EXCEL_SELECTABLE_COLUMN_TYPES: Array<ColumnConfig['type']> = [
  ...SELECTABLE_COLUMN_TYPES,
  'endorsementCompany',
  'endorsementDate',
]

// 集計表で既定ONにする表示項目（v1 summaryColumnConfigs_v20260518 の既定値。全項目ONだと列幅が
// 潰れて読めなくなるため、重要な項目だけを既定ONにする）
export const DEFAULT_ACTIVE_COLUMN_TYPES: Array<ColumnConfig['type']> = [
  'manufacturerName',
  'steelType',
  'size',
  'weight',
]

// 使用鋼材一覧表（Excel）で既定ONにする表示項目（v1 excelColumnConfigs_v20260518 の既定値）。
// Excel は列が多くてもレイアウトが崩れないため、形状（sectionType）・備考（memo）以外を既定ONにする
export const EXCEL_DEFAULT_ACTIVE_COLUMN_TYPES: Array<ColumnConfig['type']> = [
  'manufacturerName',
  'steelType',
  'no',
  'constructionArea',
  'member',
  'buildingPart',
  'orderNumber',
  'size',
  'thicknessDiameter',
  'length',
  'quantity',
  'weight',
  'heatNumber',
  'individualNumber',
  'tradingCompany',
  'innerNo',
]

export function initialSummaryColumnConfigs(): ColumnConfig[] {
  return SELECTABLE_COLUMN_TYPES.map((type) => ({ type, active: DEFAULT_ACTIVE_COLUMN_TYPES.includes(type) }))
}

export function initialExcelColumnConfigs(): ColumnConfig[] {
  return SELECTABLE_COLUMN_TYPES.map((type) => ({ type, active: EXCEL_DEFAULT_ACTIVE_COLUMN_TYPES.includes(type) }))
}

// ホームの Excel の見出し。発注照合と共有の columnTypeLabel は「裏書会社名」だが、
// ホームでは階層つきの見出し（裏書会社1〜6）と表記を揃えて「裏書会社」にする
export const HOME_EXCEL_COLUMN_LABEL_OVERRIDES: Partial<Record<ColumnConfig['type'], string>> = {
  endorsementCompany: '裏書会社',
}

// ホームの Excel 用。裏書は既定OFF（利用者が必要なときだけ選ぶ）
export function initialHomeExcelColumnConfigs(): ColumnConfig[] {
  return HOME_EXCEL_SELECTABLE_COLUMN_TYPES.map((type) => ({
    type,
    active: EXCEL_DEFAULT_ACTIVE_COLUMN_TYPES.includes(type),
  }))
}

// 並び替えできない列（「順番固定」と「その他」をまとめたもの）。表示項目設定では ON/OFF だけを出す
export const NON_REORDERABLE_COLUMN_TYPES: Array<ColumnConfig['type']> = SELECTABLE_COLUMN_TYPES.filter(
  (type) => !REORDERABLE_COLUMN_TYPES.includes(type),
)

// 列の順序を固定する関数（v1 fixColumnOrder と同じ）
// 1. ページ番号を最初に配置（常に表示）
// 2. 入れ替え可能な列を columnConfigs の順序で配置
// 3. 固定順序の列を指定された順序で配置
// 4. どちらのグループにも属さない列（商社・内部番号）を columnConfigs の順序で末尾に配置
export function fixColumnOrderFor(columnConfigs: ColumnConfig[]): ColumnConfig[] {
  const activeConfigs = columnConfigs.filter((config) => config.active && config.type !== 'pageNumber')

  return [
    { type: 'pageNumber', active: true },
    ...activeConfigs.filter((config) => REORDERABLE_COLUMN_TYPES.includes(config.type)),
    ...FIXED_ORDER_COLUMN_TYPES.flatMap((fixedType) => activeConfigs.filter((config) => config.type === fixedType)),
    ...activeConfigs.filter((config) => isOtherColumnType(config.type)),
  ]
}

// 集計表の項目名はホームでは共通ラベル（columnTypeLabel）のままでよい（発注照合の「商社名」上書きは不要）
export const printColumnTypeLabel: Record<ColumnConfig['type'], string> = columnTypeLabel

// 集計表（A4 1枚に横並びで収める帳票）でレイアウト崩れが出始める列数（ページ番号込み）。
// order-matching/print/print-columns.ts と同じ閾値
export const SUMMARY_COLUMN_COUNT_WARNING_THRESHOLD = 6

// 1ページ分の明細行数（v1 の集計表と同じ）
export const SLICE_NUMBER = 18

// useLocalStorageState の復元用 schema。v1 が保存した設定は選べる列の一部しか持っていない
// （例: v1 の集計表設定は6列ぶんしか保存していない）ため、そのまま復元すると v2 で増えた項目が
// 設定UIに出てこない。保存側の並び順・ON/OFF は尊重したまま、不足している列を既定値で末尾に補う
// （発注照合の restoredExportColumnConfigsSchema と同じ考え方）
export function restoredMillsheetColumnConfigsSchema(defaults: ColumnConfig[]) {
  return columnConfigSchema.array().transform((saved) => withMissingColumnConfigs(saved, defaults))
}
